/**
 * Install as an app.
 *
 * A CAD program that lives in a tab is a CAD program with somebody else's
 * toolbar above it and a row of unrelated tabs above that. Installed, it gets
 * its own window, its own icon, and its own place in the task switcher — and it
 * opens on a double click rather than on remembering a URL.
 *
 * The browser will not put that offer where anyone can see it. Chrome hides it
 * behind an icon at the end of the address bar; Edge puts it in a menu. What it
 * *does* do is tell the page it could be installed, through
 * `beforeinstallprompt` — and a page that catches that event may put the offer
 * wherever it likes and ask for the real prompt later. That is what this is.
 *
 * ## When it shows
 *
 * Only in an ordinary browser tab, and only where the browser has said the app
 * can be installed. Running in the installed window it shows nothing, which is
 * the point — there is nothing left to offer.
 *
 * The banner appears once. "Once" is a preference rather than a session flag,
 * because a banner that returns every time you open your work is a banner you
 * learn to close without reading. The button stays in the title bar for as long
 * as the offer stands, and the command is on the Help menu, so nothing is lost
 * by dismissing it.
 *
 * ## The browsers that never fire the event
 *
 * Safari and Firefox implement no such thing. Safari can still install — it
 * calls it Add to Dock, or Add to Home Screen — but only the person at the
 * keyboard can start it, so there the button explains where the menu item is
 * instead of prompting. Firefox on the desktop cannot install at all, and is
 * told so rather than sent looking.
 */

import { button, buttons, openDialog } from './dialog.ts';

/**
 * The event Chrome and Edge fire when the app could be installed.
 *
 * Not in the DOM library's types: it is a Chromium extension to the platform
 * rather than a standard, which is also why nothing here assumes it will come.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallOptions {
  /** Where the title-bar button goes — inserted before the Save button. */
  slot: HTMLElement;
  /** Where a banner may be put, across the top of the drawing. */
  banner: HTMLElement;
  /** True once the banner has been shown and dismissed. */
  seen: boolean;
  /** Remember that, so it is not shown again. */
  onSeen(): void;
  setMessage(text: string): void;
}

export interface InstallUi {
  /**
   * Offer it now, whatever the browser has said — the Help ▸ Install command.
   *
   * Always answers something. Where there is a real prompt it asks it; where
   * there is not it says why, which is more use than a command that appears to
   * do nothing.
   */
  offer(): void;
  /** True while there is something worth offering, for the menu's greying. */
  available(): boolean;
}

/** Already running as an app, by any of the three ways of saying so. */
export function isInstalled(): boolean {
  // `standalone` is Safari's, and predates the media query by a decade.
  const safari = (navigator as { standalone?: boolean }).standalone === true;
  if (safari) return true;
  const modes = ['standalone', 'minimal-ui', 'window-controls-overlay', 'fullscreen'];
  return modes.some((m) => {
    try { return window.matchMedia(`(display-mode: ${m})`).matches; } catch { return false; }
  });
}

/** What this browser can do about installing, when it will not tell us itself. */
function manualRoute(): { can: boolean; title: string; steps: string[] } {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua)
    // An iPad reports itself as a Mac; the touch points give it away.
    || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const webkit = /Safari/.test(ua) && !/Chrome|Chromium|Edg\//.test(ua);

  if (iOS) {
    return {
      can: true,
      title: 'Add SirCAD to your home screen',
      steps: [
        'Tap the Share button in Safari — the square with an arrow out of it.',
        'Scroll down the list and tap Add to Home Screen.',
        'Tap Add. SirCAD then opens in its own window, without the browser around it.',
      ],
    };
  }
  if (webkit) {
    return {
      can: true,
      title: 'Add SirCAD to your Dock',
      steps: [
        'Open the File menu in Safari.',
        'Choose Add to Dock.',
        'Confirm the name. SirCAD then opens in its own window, without the browser around it.',
      ],
    };
  }
  if (/Firefox/.test(ua)) {
    return {
      can: false,
      title: 'Firefox cannot install this',
      steps: [
        'Firefox on the desktop has no way to install a web app — the feature '
        + 'was removed and has not come back.',
        'Chrome, Edge and Safari can all do it, and SirCAD keeps your work in '
        + 'this browser rather than in the installed window, so nothing moves '
        + 'with you if you switch.',
      ],
    };
  }
  return {
    can: false,
    title: 'This browser has not offered to install SirCAD',
    steps: [
      'The offer appears on its own once the browser is satisfied the app can '
      + 'be installed — that needs a secure (https) connection, and usually a '
      + 'second visit.',
      'Look for an install icon at the right-hand end of the address bar, or '
      + 'Install SirCAD in the browser’s own menu.',
    ],
  };
}

/**
 * Watch for the offer and put it where it can be seen.
 *
 * Returns the handle the Help command uses. Everything else it does, it does
 * on its own from here.
 */
export function installUi(opts: InstallOptions): InstallUi {
  /** The captured event, which is the only thing that can raise a real prompt. */
  let offer: BeforeInstallPromptEvent | null = null;
  let installed = isInstalled();

  // --- The title-bar button -------------------------------------------------

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'btn install-btn';
  btn.hidden = true;
  btn.title = 'Install SirCAD as an app — its own window, its own icon';
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('aria-hidden', 'true');
  const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  // Down into a tray: the one drawing every browser uses for this.
  arrow.setAttribute('d', 'M12 3v10m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2');
  icon.append(arrow);
  btn.append(icon, Object.assign(document.createElement('span'), {
    textContent: 'Install app',
  }));
  opts.slot.prepend(btn);

  // --- The banner -----------------------------------------------------------

  let banner: HTMLElement | null = null;
  // Kept here as well as in the preferences. The browser may fire its offer
  // more than once in a session — on a reload, or when it changes its mind
  // about the app — and reading the flag we were handed at start-up would put
  // the banner back up after it had been answered.
  let seen = opts.seen;

  const dropBanner = (remember: boolean): void => {
    banner?.remove();
    banner = null;
    if (!remember) return;
    seen = true;
    opts.onSeen();
  };

  const showBanner = (): void => {
    if (banner || seen || installed) return;
    banner = document.createElement('div');
    banner.className = 'install-banner';

    const mark = document.createElement('img');
    mark.className = 'install-banner-mark';
    mark.src = './icon-192.png';
    mark.alt = '';

    const text = document.createElement('div');
    text.className = 'install-banner-text';
    const head = document.createElement('strong');
    head.textContent = 'Install SirCAD as an app';
    const sub = document.createElement('span');
    sub.textContent = 'Its own window and its own icon, with no browser around it. '
      + 'Your work stays exactly where it is.';
    text.append(head, sub);

    const yes = document.createElement('button');
    yes.type = 'button';
    yes.className = 'btn primary';
    yes.textContent = 'Install';
    yes.addEventListener('click', () => { dropBanner(true); void ask(); });

    const no = document.createElement('button');
    no.type = 'button';
    no.className = 'btn';
    no.textContent = 'Not now';
    // Remembered: the button in the title bar carries the offer from here on,
    // and asking the same question twice is how a banner becomes furniture.
    no.addEventListener('click', () => dropBanner(true));

    banner.append(mark, text, yes, no);
    opts.banner.append(banner);
  };

  // --- Asking ---------------------------------------------------------------

  const explain = (): void => {
    const route = manualRoute();
    openDialog({
      title: route.title,
      build(body, handle) {
        if (route.can) {
          const lead = document.createElement('p');
          lead.className = 'modal-hint';
          lead.textContent = 'This browser does not let a page raise the install '
            + 'prompt itself, so it takes three steps by hand:';
          body.append(lead);
          const list = document.createElement('ol');
          list.className = 'install-steps';
          for (const step of route.steps) {
            const li = document.createElement('li');
            li.textContent = step;
            list.append(li);
          }
          body.append(list);
        } else {
          for (const line of route.steps) {
            const p = document.createElement('p');
            p.className = 'modal-hint';
            p.textContent = line;
            body.append(p);
          }
        }
        button(buttons(body), 'Close', () => handle.close());
      },
    });
  };

  /** Raise the real prompt if there is one, and explain if there is not. */
  const ask = async (): Promise<void> => {
    if (installed) { opts.setMessage('SirCAD is already installed'); return; }
    if (!offer) { explain(); return; }

    const event = offer;
    // Spent either way: the browser allows one prompt per event, and holding a
    // used one would give a button that silently does nothing the second time.
    offer = null;
    btn.hidden = true;
    try {
      await event.prompt();
      const { outcome } = await event.userChoice;
      if (outcome === 'accepted') {
        opts.setMessage('Installing SirCAD — it will open in its own window');
        return;
      }
      // Declined is not refused forever. The button comes back, because the
      // answer to "not now" is to be there later.
      btn.hidden = false;
      offer = event;
      opts.setMessage('Not installed — the button stays in the title bar');
    } catch {
      btn.hidden = false;
      opts.setMessage('The browser would not show the install prompt');
    }
  };

  btn.addEventListener('click', () => void ask());

  // --- What the browser tells us --------------------------------------------

  window.addEventListener('beforeinstallprompt', (ev) => {
    // Held rather than let go: unprevented, Chrome shows its own small prompt
    // now and will not offer again for months. Caught, the offer is ours to
    // make when and where it reads as part of the program.
    ev.preventDefault();
    if (installed) return;
    offer = ev as BeforeInstallPromptEvent;
    btn.hidden = false;
    showBanner();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    offer = null;
    btn.hidden = true;
    dropBanner(true);
    opts.setMessage('SirCAD installed — you can open it from your applications now');
  });

  // Installed while this window was open, or opened straight into the app: the
  // media query changes under a running page, so it is watched rather than read
  // once at start-up.
  try {
    window.matchMedia('(display-mode: standalone)')
      .addEventListener('change', (ev) => {
        if (!ev.matches) return;
        installed = true;
        btn.hidden = true;
        dropBanner(false);
      });
  } catch { /* older browsers: the event listener form is not there */ }

  return {
    offer: () => void ask(),
    available: () => !installed,
  };
}

/**
 * Register the service worker.
 *
 * Not for offline alone — Chrome will not fire `beforeinstallprompt` at all
 * without one, so the install offer above depends on this having run.
 *
 * The version goes on the URL. The browser decides whether to fetch a worker
 * again by comparing the script *URL*, so a build that changed nothing about
 * `sw.js` itself would otherwise keep serving the last build's cache.
 *
 * Failure is not reported. It fails on http, in private windows and wherever
 * the user has turned workers off — none of which is a fault, and in all of
 * them the app works exactly as it did before there was a worker.
 */
export function registerWorker(version: string): void {
  if (!('serviceWorker' in navigator)) return;
  // After load, so it never competes with the app's own bundle for the network
  // on a first visit.
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register(`./sw.js?v=${encodeURIComponent(version)}`, { scope: './' })
      .catch(() => { /* not available here; the app does not need it */ });
  });
}
